import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface MobileTestHelperProps {
  showInProduction?: boolean
}

export function MobileTestHelper({ showInProduction = false }: MobileTestHelperProps) {
  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    touchSupport: false,
    orientation: 'portrait' as 'portrait' | 'landscape'
  })
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight
      })

      setDeviceInfo({
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
        touchSupport: 'ontouchstart' in window,
        orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
      })
    }

    updateViewport()
    window.addEventListener('resize', updateViewport)
    window.addEventListener('orientationchange', updateViewport)

    return () => {
      window.removeEventListener('resize', updateViewport)
      window.removeEventListener('orientationchange', updateViewport)
    }
  }, [])

  // Only show in development unless explicitly enabled for production
  if (!showInProduction && process.env.NODE_ENV === 'production') {
    return null
  }

  const getBreakpointInfo = () => {
    const { width } = viewport
    if (width < 320) return { name: 'xs-small', color: 'text-red-600' }
    if (width < 640) return { name: 'xs (mobile)', color: 'text-orange-600' }
    if (width < 768) return { name: 'sm', color: 'text-yellow-600' }
    if (width < 1024) return { name: 'md (tablet)', color: 'text-blue-600' }
    if (width < 1200) return { name: 'lg', color: 'text-green-600' }
    return { name: 'xl+ (desktop)', color: 'text-purple-600' }
  }

  const breakpoint = getBreakpointInfo()

  if (!isVisible) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsVisible(true)}
        className="fixed bottom-20 right-4 z-40 opacity-50 hover:opacity-100"
      >
        📱 Test
      </Button>
    )
  }

  return (
    <Card className="fixed bottom-4 right-4 z-50 max-w-xs mobile-fade-in">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Mobile Test Info</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(false)}
            className="h-6 w-6 p-0"
          >
            ×
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        <div>
          <span className="font-medium">Viewport:</span> {viewport.width} × {viewport.height}
        </div>
        <div>
          <span className="font-medium">Breakpoint:</span>{' '}
          <span className={breakpoint.color}>{breakpoint.name}</span>
        </div>
        <div>
          <span className="font-medium">Device:</span>{' '}
          {deviceInfo.isMobile ? 'Mobile' : deviceInfo.isTablet ? 'Tablet' : 'Desktop'}
        </div>
        <div>
          <span className="font-medium">Touch:</span>{' '}
          {deviceInfo.touchSupport ? '✅' : '❌'}
        </div>
        <div>
          <span className="font-medium">Orientation:</span> {deviceInfo.orientation}
        </div>
        <div className="pt-2 border-t">
          <div className="text-xs text-muted-foreground">
            <div>• Touch targets: 44px+ ✅</div>
            <div>• Min font: 16px ✅</div>
            <div>• PWA ready ✅</div>
            <div>• Offline support ✅</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}